import { useMemo, useSyncExternalStore } from 'react';
import { isCurrentFeedbackDraftScope } from '@/lib/api';
import type { FeedbackDraftScope } from '@/lib/types';
import { useAuthStore } from '@/stores/auth';
import { useScopeStore } from '@/stores/scope';

/** Departure permanently retires this mounted form, including leave-and-return
 * within one React batch. A new authorized entry must mount a fresh form. */
export function useFeedbackAuthoringScope(scope: FeedbackDraftScope): boolean {
  return useFeedbackAuthoringBoundary(scope).active;
}

export function useFeedbackAuthoringBoundary(scope: FeedbackDraftScope) {
  const boundary = useMemo(() => {
    const captured = { ...scope };
    let departed = false;
    const current = () => {
      if (!isCurrentFeedbackDraftScope(captured)) departed = true;
      return !departed;
    };
    return { current, subscribe: (notify: () => void) => {
      const changed = () => { current(); notify(); };
      const auth = useAuthStore.subscribe(changed), selection = useScopeStore.subscribe(changed);
      changed();
      return () => { auth(); selection(); };
    } };
    // The caller's captured context is fixed for the life of this entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const active = useSyncExternalStore(boundary.subscribe, boundary.current, () => false)
    && isCurrentFeedbackDraftScope(scope);
  return { active, current: boundary.current };
}
